/**
 * `/v1/securities/:ticker` and its two fan-out routes, in local mode (plan §6.6).
 *
 * Local mode is the ONE place `consensus` and `catalysts` carry values. Live v1
 * has no licensed consensus provider and does not invent headlines about real
 * companies, so `HttpRepository` returns `consensus: null` and `catalysts: []`
 * and the UI renders documented empty states (plan §6.5a). That asymmetry is
 * asserted per adapter and deliberately kept OUT of the shared port-conformance
 * factory, which asserts shape parity, not value parity (plan §4.7).
 */
import type {
  DiversificationPayload,
  Security,
  SecurityProfile,
  SeriesQuery,
  Ticker,
} from '@meridian/contracts';

import { computeCatalysts } from '../../analytics/catalysts';
import { computeConsensus } from '../../analytics/consensus';
import { rankByCorrelation } from '../../analytics/correlation';
import { correlationExtremesFromRanked } from '../../analytics/correlation-extremes';
import { diversificationGroupsFromRanked } from '../../analytics/diversification';
import { selectPeers } from '../../analytics/peers';
import { CORRELATION_EXTREMES_SIZE, PEER_PANEL_SIZE } from '../../constants/panels';
import { generateSeries } from '../../series/generate-series';
import { seriesStats } from '../../series/series-stats';
import { toWireSecurity, type DerivedSecurity } from '../../universe/derive-security';

import type { SeriesCache } from '../../series/series-cache';

/** 52-week statistics are, definitionally, the one-year window. */
const STATS_WINDOW = '1Y' as const;

export function buildProfile(security: DerivedSecurity, cache: SeriesCache): SecurityProfile {
  return {
    security: toWireSecurity(security),
    stats: seriesStats(generateSeries(security, STATS_WINDOW, cache)),
    consensus: computeConsensus(security),
    catalysts: computeCatalysts(security, STATS_WINDOW),
  };
}

export function buildPeers(
  rows: readonly DerivedSecurity[],
  security: DerivedSecurity,
  query: SeriesQuery,
): readonly Security[] {
  return selectPeers(security, rows, query.window, PEER_PANEL_SIZE).map(toWireSecurity);
}

export function buildDiversifiers(
  rows: readonly DerivedSecurity[],
  security: DerivedSecurity,
  query: SeriesQuery,
): DiversificationPayload {
  const candidates = rows.filter((r) => r.ticker !== security.ticker);
  /* One pairwise scan feeds both the extremes shortlists and the three groups
     (contracts: same pairwise correlation definition and shared scan). */
  const ranked = rankByCorrelation(security, candidates);
  const { most, least } = correlationExtremesFromRanked(ranked, CORRELATION_EXTREMES_SIZE);
  return {
    ticker: security.ticker,
    window: query.window,
    /* `core` returns readonly arrays; the wire schemas type them mutable. The
       spread is the boundary copy, not a cast — the same reason
       `toWireSecurity` rebuilds `idx`/`f`: handing a consumer the array core
       computed over makes an in-place sort three layers away corrupt this
       payload. */
    groups: [...diversificationGroupsFromRanked(security, ranked, query.window)],
    most: [...most],
    least: [...least],
  };
}

export function indexByTicker(
  rows: readonly DerivedSecurity[],
): ReadonlyMap<Ticker, DerivedSecurity> {
  return new Map(rows.map((r) => [r.ticker, r]));
}
